import { Action } from '@utils/reducers/Action'
import {
  Survey,
  FlatQuestion,
  AnswerObject,
  Question,
  getCategoryQuestions,
  withChoicesFromApi,
} from '@models/Survey'
import { AppStatus, RequestState } from '@models/Common'
import { surveyStorage, SurveyStorageKeys } from '@plugins/storage'
import { SurveyStep } from '@models/Survey/SurveyStep'
import { AnswerType } from '@models/Survey/Answer'
import { Choice, QuestionType, TacticalQuestion, TacticalQuestionMeta } from '@models/Survey/Survey'
import { TacticalQuestionClass } from '@models/Tactical'
import i18n from '../i18n'
import environment from 'config/environment'
import { logReducerAction } from '../dev/devSessionLog'
import {
  QuestionnairePhase,
  questionnairePhaseForSurvey,
  resolveResultVariant,
} from '@utils/categoryFlow'
import {
  isQuestionVisible,
  nextVisibleIndex,
  prevVisibleIndex,
  visibleCount,
  visiblePosition,
  visibleQuestions,
} from '@utils/showIf'

export enum SurveyMode {
  DEFAULT,
  RESULTS,
  SANDBOX,
  SANDBOX_PORTRAIT,
  PROBABILITY,
}

export type SurveyState = RequestState & {
  answerObject: AnswerObject
  currentPosition?: number
  currentQuestion?: FlatQuestion
  currentAnswer?: AnswerType
  hasAcknowledged?: boolean
  nextIndex?: number
  previousIndex?: number
  questionsCount: number
  questionsList: FlatQuestion[]
  questionIds: string[]
  survey?: Survey
  surveyIsCompleted: boolean
  mode: SurveyMode
  step: SurveyStep
  enabled: boolean
  surveys: Survey[]
  startedSurveyAt?: string
  hasSentStartSurvey: boolean
  currentSurveyId?: string
  questionnairePhase?: QuestionnairePhase
  resultVariant?: 'intermediate' | 'final'
  /** Email captured after Email step (not linked to answers; for progress tooltip). */
  email?: string
  /** Portrait thumbnails keyed by survey key (edition progress tooltip / modal). */
  portraitBySurveyKey?: Record<string, string>
  /** Rectify branch taken — include rectify segment in edition progress track. */
  hasVisitedRectify?: boolean
  /** Intermediate/final prediction snapshots for progress tooltip mini-charts. */
  resultsSnapshotsBySurveyKey?: Record<string, unknown>
  /** Visible vs total question counts per survey (conditionals → visible ≤ total). */
  questionnaireStatsBySurveyKey?: Record<string, { visibleCount: number; totalCount: number }>
  /** Answers frozen per survey key (multi-survey sandbox switcher). */
  answersBySurveyKey?: Record<string, AnswerObject>
  /** Survey payloads cached for sandbox questionnaire switching. */
  surveyCacheByKey?: Record<string, Survey>
}

export const initialState: SurveyState = {
  status: AppStatus.IDLE,
  errorMessage: undefined,
  answerObject: {},
  questionsCount: 0,
  questionsList: [],
  questionIds: [],
  surveyIsCompleted: false,
  mode: SurveyMode.DEFAULT,
  step: SurveyStep.Answer,
  enabled: false,
  surveys: [],
  startedSurveyAt: undefined,
  hasSentStartSurvey: false,
  currentSurveyId: undefined,
  questionnairePhase: 'q1',
  resultVariant: undefined,
  email: undefined,
  portraitBySurveyKey: {},
  hasVisitedRectify: false,
  resultsSnapshotsBySurveyKey: {},
  questionnaireStatsBySurveyKey: {},
  answersBySurveyKey: {},
  surveyCacheByKey: {},
}

/** Persist current answers + survey payload under the active survey key. */
function syncSurveyKeyedCaches(state: SurveyState): SurveyState {
  const key = state.survey?.key
  if (!key) {
    return state
  }
  return {
    ...state,
    answersBySurveyKey: {
      ...(state.answersBySurveyKey ?? {}),
      [key]: state.answerObject ?? {},
    },
    surveyCacheByKey: state.survey
      ? {
          ...(state.surveyCacheByKey ?? {}),
          [key]: state.survey,
        }
      : state.surveyCacheByKey,
  }
}

function withQuestionnaireStats(state: SurveyState): SurveyState {
  const key = state.survey?.key
  if (!key || !state.questionsList.length) {
    return state
  }
  const totalCount = state.questionsList.length
  const computedVisible = visibleCount(state.questionsList, state.answerObject)
  const visible =
    state.questionsCount > 0 ? state.questionsCount : computedVisible
  const prev = state.questionnaireStatsBySurveyKey?.[key]
  // Never overwrite a good freeze with empty/zero counts (e.g. after answer reset).
  if (totalCount <= 0) {
    return state
  }
  if (prev && prev.totalCount > 0 && visible <= 0 && prev.visibleCount > 0) {
    return {
      ...state,
      questionnaireStatsBySurveyKey: {
        ...(state.questionnaireStatsBySurveyKey ?? {}),
        [key]: { visibleCount: prev.visibleCount, totalCount },
      },
    }
  }
  return {
    ...state,
    questionnaireStatsBySurveyKey: {
      ...(state.questionnaireStatsBySurveyKey ?? {}),
      [key]: {
        visibleCount: visible > 0 ? visible : prev?.visibleCount || totalCount,
        totalCount,
      },
    },
  }
}

/** Freeze current survey path counts (used when leaving Answer / switching survey). */
function freezeCurrentQuestionnaireStats(state: SurveyState): SurveyState {
  return withQuestionnaireStats(state)
}

const POST_ANSWER_STEPS = new Set<SurveyStep>([
  SurveyStep.Email,
  SurveyStep.Acknowledge,
  SurveyStep.Prediction,
  SurveyStep.Results,
  SurveyStep.ResultsIntermediate,
])

export function pruneAnswerObject(answerObject: AnswerObject, questionsList: FlatQuestion[]): AnswerObject {
  if (!questionsList.length) {
    return {}
  }
  const validIds = new Set(questionsList.map((q) => q.id))
  return Object.fromEntries(Object.entries(answerObject).filter(([id]) => validIds.has(id)))
}

export function computeSurveyIsCompleted(
  answerObject: AnswerObject,
  questionsList: FlatQuestion[],
): boolean {
  return visibleQuestions(questionsList, answerObject).every(
    (question) => question.optional || answerObject[question.id] !== undefined,
  )
}

const setCurrentQuestion = (state: SurveyState, index?: number): SurveyState => {
  if (index === undefined || state.questionsList.length === 0) return state
  const { answerObject, questionsList } = state
  let targetIndex = index
  if (!questionsList[targetIndex]) return state

  if (!isQuestionVisible(questionsList[targetIndex], answerObject, questionsList)) {
    const next = nextVisibleIndex(questionsList, targetIndex - 1, answerObject)
    const prev = prevVisibleIndex(questionsList, targetIndex + 1, answerObject)
    const fallback = next ?? prev
    if (fallback === undefined) {
      // Never display a hidden question (e.g. admin gotoQuestion / stale progress index).
      const currentId = state.currentQuestion?.id
      const currentIdx = currentId ? questionsList.findIndex((q) => q.id === currentId) : -1
      if (
        currentIdx >= 0 &&
        isQuestionVisible(questionsList[currentIdx], answerObject, questionsList)
      ) {
        targetIndex = currentIdx
      } else {
        const firstVisible = questionsList.findIndex((q) =>
          isQuestionVisible(q, answerObject, questionsList),
        )
        if (firstVisible < 0) return state
        targetIndex = firstVisible
      }
    } else {
      targetIndex = fallback
    }
  }

  const currentQuestion = questionsList[targetIndex]
  if (!currentQuestion) return state
  const count = visibleCount(questionsList, answerObject)
  const position = visiblePosition(questionsList, targetIndex, answerObject)
  return withQuestionnaireStats({
    ...state,
    currentQuestion,
    previousIndex: prevVisibleIndex(questionsList, targetIndex, answerObject),
    nextIndex: nextVisibleIndex(questionsList, targetIndex, answerObject),
    currentPosition: position || count,
    questionsCount: count,
    currentAnswer: answerObject[currentQuestion.id] ?? undefined,
  })
}

function reconcileSurveyAnswers(state: SurveyState): SurveyState {
  const answerObject = pruneAnswerObject(state.answerObject ?? {}, state.questionsList)
  let next: SurveyState = {
    ...state,
    answerObject,
    surveyIsCompleted: computeSurveyIsCompleted(answerObject, state.questionsList),
    questionsCount: visibleCount(state.questionsList, answerObject),
  }

  if (!next.surveyIsCompleted && POST_ANSWER_STEPS.has(next.step)) {
    next = {
      ...next,
      step: SurveyStep.Answer,
      hasAcknowledged: undefined,
    }
  }

  if (next.currentQuestion?.id) {
    const index = next.questionsList.findIndex((q) => q.id === next.currentQuestion?.id)
    if (index >= 0) {
      next = setCurrentQuestion(next, index)
    } else {
      next = {
        ...next,
        currentAnswer: answerObject[next.currentQuestion.id] ?? undefined,
      }
    }
  }

  return withQuestionnaireStats(next)
}

export enum SurveyActionType {
  FetchSurveyFailure = 'FetchSurveyFailure',
  FetchSurveyPending = 'FetchSurveyPending',
  FetchSurveySuccess = 'FetchSurveySuccess',
  LoadSurveyProgress = 'LoadSurveyProgress',
  ResetAnswers = 'ResetAnswers',
  SetAcknowledge = 'SetAcknowledge',
  SetAnswerObject = 'SetAnswerObject',
  SetCurrentAnswer = 'SetCurrentAnswer',
  SetCurrentQuestion = 'SetCurrentQuestion',
  SetSurveys = 'SetSurveys',
  SetCurrentSurveyId = 'SetCurrentSurveyId',
  SetMode = 'SetMode',
  SetNextQuestion = 'SetNextQuestion',
  SetStep = 'SetStep',
  SetPreviousQuestion = 'SetPreviousQuestion',
  SetSurveyProgress = 'SetSurveyProgress',
  SetIsEnabled = 'SetIsEnabled',
  SetStartedSurveyAt = 'SetStartedSurveyAt',
  SetHasSentStartSurvey = 'SetHasSentStartSurvey',
  ResetSandbox = 'ResetSandbox',
  SetQuestionnairePhase = 'SetQuestionnairePhase',
  SetResultVariant = 'SetResultVariant',
  PrepareSecondQuestionnaire = 'PrepareSecondQuestionnaire',
  SwitchSandboxSurvey = 'SwitchSandboxSurvey',
  SetEmail = 'SetEmail',
  SetPortraitForSurvey = 'SetPortraitForSurvey',
  SetHasVisitedRectify = 'SetHasVisitedRectify',
  SetResultsSnapshot = 'SetResultsSnapshot',
}

export const flattenSurvey = (survey: Survey, surveyState: SurveyState): FlatQuestion[] => {
  const category = survey.category
  const categoryId = category?.id
  if (!categoryId) {
    return []
  }

  return getCategoryQuestions(category).map((raw) => {
    const question = withChoicesFromApi(raw)
    const subQuestions = (question.meta as TacticalQuestionMeta).subQuestions
    if (subQuestions && subQuestions.length > 0) {
      return {
        ...transformTacticalQuestion(new TacticalQuestionClass(question as TacticalQuestion), surveyState),
        category: categoryId,
      }
    }
    return {
      ...question,
      category: categoryId,
    }
  })
}

const transformTacticalQuestion = (tacticalQuestion: TacticalQuestionClass, surveyState: SurveyState) => {
  const chosenId = tacticalQuestion.hasBeenChosenAlready(surveyState)
  if (chosenId) {
    const alreadyChosenTacticalQuestion = surveyState.questionsList.find((q) => q.id === chosenId)
    if (alreadyChosenTacticalQuestion) {
      return alreadyChosenTacticalQuestion
    }
  }

  const randomQuestion = tacticalQuestion.getRandomSubQuestionByProbability()

  return { ...tacticalQuestion, key: randomQuestion?.key, text: randomQuestion?.text } as Question
}

const initSurvey = (state: SurveyState, survey: Survey): SurveyState => {
  const questionsList = flattenSurvey(survey, state)

  if (questionsList.length === 0) return state

  const questionIds = questionsList.map((question) => question.id)
  const answerObject = pruneAnswerObject(state.answerObject ?? {}, questionsList)
  const firstVisible =
    questionsList.findIndex((q) => isQuestionVisible(q, answerObject, questionsList))
  const startIndex = firstVisible >= 0 ? firstVisible : 0

  return reconcileSurveyAnswers({
    ...state,
    status: AppStatus.IDLE,
    errorMessage: undefined,
    survey,
    questionsList,
    questionIds,
    answerObject,
    currentQuestion: questionsList[startIndex],
    currentPosition: visiblePosition(questionsList, startIndex, answerObject) || 1,
    questionsCount: visibleCount(questionsList, answerObject),
    nextIndex: nextVisibleIndex(questionsList, startIndex, answerObject),
    previousIndex: prevVisibleIndex(questionsList, startIndex, answerObject),
  })
}

const setStep = (state: SurveyState, nextStep: SurveyStep): SurveyState => {
  return { ...state, step: nextStep }
}

const loadSurveyProgress = (state: SurveyState, stateInProgress: SurveyState): SurveyState => {
  if (!stateInProgress) {
    return { ...state }
  }

  const storedSurvey = stateInProgress.survey
  const currentSurvey = state.survey ?? storedSurvey
  // Prefer question id — currentPosition is a visible 1-based index, not a full-list index.
  const storedQuestionId = stateInProgress.currentQuestion?.id
  const positionIndexFromId = storedQuestionId
    ? state.questionsList.findIndex((q) => q.id === storedQuestionId)
    : -1
  const positionIndex =
    positionIndexFromId >= 0
      ? positionIndexFromId
      : Math.max(0, (stateInProgress.currentPosition ?? 1) - 1)

  const surveyVersionChanged =
    storedSurvey?.id &&
    currentSurvey?.id &&
    storedSurvey.id === currentSurvey.id &&
    storedSurvey.version !== currentSurvey.version

  if (surveyVersionChanged) {
    const reinitialized = initSurvey(
      {
        ...state,
        answerObject: {},
        step: SurveyStep.Answer,
        hasAcknowledged: undefined,
        currentPosition: 1,
      },
      currentSurvey as Survey,
    )
    return setCurrentQuestion(reinitialized, 0)
  }

  if (storedSurvey?.id && storedSurvey.id !== state.survey?.id) {
    const reinitialized = initSurvey(
      {
        ...stateInProgress,
        answerObject: stateInProgress.answerObject ?? {},
      },
      storedSurvey,
    )
    return setCurrentQuestion(reinitialized, positionIndex)
  }

  const merged = reconcileSurveyAnswers({
    ...stateInProgress,
    questionsList: state.questionsList,
    questionIds: state.questionIds,
    questionsCount: state.questionsCount,
    survey: currentSurvey,
  })
  return setCurrentQuestion(merged, positionIndex)
}

const setFromId = (state: SurveyState, slug: string): SurveyState => {
  const currentQuestion = state.questionsList.find((x: Question) => x.id === slug)
  if (!currentQuestion) return state
  const index = state.questionsList.indexOf(currentQuestion)
  return setCurrentQuestion(state, index)
}

const answerQuestion = (state: SurveyState, value: AnswerType) => {
  if (!state.currentQuestion) return state
  const answerObject = { ...state.answerObject, [state.currentQuestion.id]: value }
  const index = state.questionsList.findIndex((q) => q.id === state.currentQuestion?.id)
  const withAnswers = {
    ...state,
    answerObject,
    surveyIsCompleted: computeSurveyIsCompleted(answerObject, state.questionsList),
    currentAnswer: value,
  }
  // Refresh next/prev/progress after visibility may have changed.
  const next = index >= 0 ? setCurrentQuestion(withAnswers, index) : withAnswers
  // Freeze path counts as soon as the questionnaire is fully answered.
  const frozen = next.surveyIsCompleted ? freezeCurrentQuestionnaireStats(next) : next
  return syncSurveyKeyedCaches(frozen)
}

const setAcknowledge = (state: SurveyState, hasAcknowledged: boolean): SurveyState => ({
  ...state,
  hasAcknowledged,
})

const setMode = (state: SurveyState, mode: SurveyMode): SurveyState => ({
  ...state,
  mode,
})

const SurveyReducer = (state: SurveyState, action: Action<SurveyActionType>): SurveyState => {
  const { payload, type } = action

  if (environment.isDev) {
    // eslint-disable-next-line no-console
    console.log(action, { reducer: 'survey', state, action })
    logReducerAction('survey', action)
  }

  switch (type) {
    case SurveyActionType.FetchSurveyPending:
      return { ...state, status: AppStatus.PENDING }
    case SurveyActionType.FetchSurveyFailure:
      return { ...state, status: AppStatus.ERROR, errorMessage: payload as string }
    case SurveyActionType.FetchSurveySuccess: {
      const loaded = payload as Survey
      const withCache = loaded.key
        ? {
            ...state,
            surveyCacheByKey: {
              ...(state.surveyCacheByKey ?? {}),
              [loaded.key]: loaded,
            },
          }
        : state
      const initialized = initSurvey(withCache, loaded)
      const startIndex = Math.max(0, (initialized.currentPosition ?? 1) - 1)
      return syncSurveyKeyedCaches({
        ...setCurrentQuestion(initialized, startIndex),
        questionnairePhase: questionnairePhaseForSurvey(loaded),
      })
    }

    case SurveyActionType.LoadSurveyProgress:
      return loadSurveyProgress(state, payload as SurveyState)

    case SurveyActionType.SetSurveyProgress:
      surveyStorage.setItem(SurveyStorageKeys.ANSWER_DATA, action.payload)
      return state

    case SurveyActionType.SetCurrentSurveyId:
      return { ...state, currentSurveyId: payload as string }

    case SurveyActionType.SetPreviousQuestion:
      return setCurrentQuestion(state, state.previousIndex)

    case SurveyActionType.SetNextQuestion:
      return setCurrentQuestion(state, state.nextIndex)

    case SurveyActionType.SetCurrentQuestion:
      return setFromId(state, payload as string)

    case SurveyActionType.SetCurrentAnswer:
      return answerQuestion(state, payload as string)

    case SurveyActionType.SetAcknowledge:
      return setAcknowledge(state, payload as boolean)

    case SurveyActionType.SetMode:
      return setMode(state, payload as SurveyMode)

    case SurveyActionType.SetAnswerObject: {
      const answerObject = payload as AnswerObject
      return syncSurveyKeyedCaches(
        reconcileSurveyAnswers({
          ...state,
          answerObject,
        }),
      )
    }

    case SurveyActionType.SetIsEnabled:
      return { ...state, enabled: payload as boolean }

    case SurveyActionType.SetStep: {
      const nextStep = payload as SurveyStep
      // Snapshot question counts before leaving the questionnaire (completed path).
      const base =
        state.step === SurveyStep.Answer && nextStep !== SurveyStep.Answer
          ? freezeCurrentQuestionnaireStats(state)
          : state
      const withRectify =
        nextStep === SurveyStep.Rectify ? { ...base, hasVisitedRectify: true } : base
      return setStep(withRectify, nextStep)
    }

    case SurveyActionType.SetQuestionnairePhase:
      return { ...state, questionnairePhase: payload as QuestionnairePhase }

    case SurveyActionType.SetResultVariant:
      return { ...state, resultVariant: payload as 'intermediate' | 'final' }

    case SurveyActionType.PrepareSecondQuestionnaire: {
      const survey = payload as Survey
      // Freeze outgoing questionnaire stats before answers/list are replaced.
      const frozen = syncSurveyKeyedCaches(freezeCurrentQuestionnaireStats(state))
      const answersBySurveyKey = { ...(frozen.answersBySurveyKey ?? {}) }
      const surveyCacheByKey = { ...(frozen.surveyCacheByKey ?? {}) }
      if (survey.key) {
        surveyCacheByKey[survey.key] = survey
      }
      const initialized = initSurvey(
        {
          ...frozen,
          answerObject: {},
          currentAnswer: undefined,
          surveyIsCompleted: false,
          step: SurveyStep.Answer,
          currentPosition: undefined,
          currentQuestion: undefined,
          nextIndex: undefined,
          previousIndex: undefined,
          // Keep email / portraits / snapshots / acknowledge / keyed caches across stages
          email: frozen.email,
          portraitBySurveyKey: frozen.portraitBySurveyKey,
          resultsSnapshotsBySurveyKey: frozen.resultsSnapshotsBySurveyKey,
          questionnaireStatsBySurveyKey: frozen.questionnaireStatsBySurveyKey,
          hasVisitedRectify: frozen.hasVisitedRectify,
          hasAcknowledged: frozen.hasAcknowledged,
          answersBySurveyKey,
          surveyCacheByKey,
        },
        survey,
      )
      return setCurrentQuestion(
        {
          ...initialized,
          questionnairePhase: questionnairePhaseForSurvey(survey),
        },
        0,
      )
    }

    case SurveyActionType.SwitchSandboxSurvey: {
      const { survey, answers } = payload as { survey: Survey; answers?: AnswerObject }
      if (!survey?.key) {
        return state
      }
      const frozen = syncSurveyKeyedCaches(freezeCurrentQuestionnaireStats(state))
      const answersBySurveyKey = { ...(frozen.answersBySurveyKey ?? {}) }
      const surveyCacheByKey = {
        ...(frozen.surveyCacheByKey ?? {}),
        [survey.key]: survey,
      }
      const restored = answers ?? answersBySurveyKey[survey.key] ?? {}
      answersBySurveyKey[survey.key] = restored
      const initialized = initSurvey(
        {
          ...frozen,
          answerObject: restored,
          currentAnswer: undefined,
          surveyIsCompleted: false,
          currentPosition: undefined,
          currentQuestion: undefined,
          nextIndex: undefined,
          previousIndex: undefined,
          email: frozen.email,
          portraitBySurveyKey: frozen.portraitBySurveyKey,
          resultsSnapshotsBySurveyKey: frozen.resultsSnapshotsBySurveyKey,
          questionnaireStatsBySurveyKey: frozen.questionnaireStatsBySurveyKey,
          hasVisitedRectify: frozen.hasVisitedRectify,
          hasAcknowledged: frozen.hasAcknowledged,
          answersBySurveyKey,
          surveyCacheByKey,
          mode: frozen.mode,
          step: frozen.step,
        },
        survey,
      )
      const phase = questionnairePhaseForSurvey(survey)
      return syncSurveyKeyedCaches(
        setCurrentQuestion(
          {
            ...initialized,
            questionnairePhase: phase,
            resultVariant: resolveResultVariant(undefined, survey, phase),
            mode: frozen.mode,
            step: frozen.step,
          },
          0,
        ),
      )
    }

    case SurveyActionType.ResetSandbox: {
      const answerObject = payload as AnswerObject
      return syncSurveyKeyedCaches(
        reconcileSurveyAnswers({
          ...state,
          answerObject,
        }),
      )
    }

    case SurveyActionType.SetEmail:
      return { ...state, email: payload as string }

    case SurveyActionType.SetPortraitForSurvey: {
      const { surveyKey, dataUrl } = payload as { surveyKey: string; dataUrl: string }
      if (!surveyKey || !dataUrl) {
        return state
      }
      return {
        ...state,
        portraitBySurveyKey: {
          ...(state.portraitBySurveyKey ?? {}),
          [surveyKey]: dataUrl,
        },
      }
    }

    case SurveyActionType.SetHasVisitedRectify:
      return { ...state, hasVisitedRectify: Boolean(payload) }

    case SurveyActionType.SetResultsSnapshot: {
      const { surveyKey, snapshot } = payload as { surveyKey: string; snapshot: unknown }
      if (!surveyKey) {
        return state
      }
      return {
        ...state,
        resultsSnapshotsBySurveyKey: {
          ...(state.resultsSnapshotsBySurveyKey ?? {}),
          [surveyKey]: snapshot,
        },
      }
    }

    case SurveyActionType.SetStartedSurveyAt:
      return { ...state, startedSurveyAt: payload as string }

    case SurveyActionType.SetHasSentStartSurvey:
      return { ...state, hasSentStartSurvey: payload as boolean }

    case SurveyActionType.ResetAnswers:
      return { ...state, answerObject: payload as AnswerObject }

    case SurveyActionType.SetSurveys: {
      const currentActiveSurvey = (payload as Survey[]).find((survey) => survey.active)

      const shouldClear =
        state.survey &&
        (state.survey?.id !== currentActiveSurvey?.id || state.survey?.version !== currentActiveSurvey?.version)
      if (shouldClear) {
        surveyStorage.clear()
        return {
          ...initialState,
          enabled: !!currentActiveSurvey,
          surveys: [...(payload as Survey[])],
        }
      }
      return {
        ...state,
        enabled: !!currentActiveSurvey,
        surveys: [...(payload as Survey[])],
      }
    }

    default:
      return state
  }
}

export default SurveyReducer
